import type { FactorConfidence } from "./factor-explanations";

export type ForecastSafetyProvenance =
  | "missing-local-geometry"
  | "missing-forecast-input"
  | "missing-route-context"
  | "missing-official-safety-input";

export type ForecastSafetyIndicator = {
  key?: string | null;
  id?: string | null;
  level?: string | null;
  severity?: string | null;
  label?: string | null;
  detail?: string | null;
  confidence?: FactorConfidence | null;
  provenance?: ForecastSafetyProvenance | null;
};

export type ConditionSafetyState =
  | "clear"
  | "flagged-amber"
  | "flagged-red"
  | "no-data";

export type SafetyNote = {
  label: string;
  detail: string | null;
  severity: "amber" | "red";
};

export type ForecastSafetyContainer =
  | ForecastSafetyIndicator[]
  | {
      indicators?: ForecastSafetyIndicator[] | null;
      safety?: ForecastSafetyIndicator[] | null;
    }
  | null
  | undefined;

export type ForecastSafetyPayload = {
  pisteSafety?: ForecastSafetyIndicator[] | null;
  avalancheSafety?: ForecastSafetyIndicator | null;
  stormHazard?: ForecastSafetyIndicator | null;
  mtbSafety?: ForecastSafetyIndicator[] | null;
  sportSafety?: Record<string, ForecastSafetyContainer> | null;
  safetyState?: Record<string, ConditionSafetyState | null | undefined> | null;
  safetyStates?: Record<string, ConditionSafetyState | null | undefined> | null;
  surfSafety?: ForecastSafetyContainer;
  windSafety?: ForecastSafetyContainer;
  windSportsSafety?: ForecastSafetyContainer;
  foilSafety?: ForecastSafetyContainer;
  downwindFoilSafety?: ForecastSafetyContainer;
  wingFoilSafety?: ForecastSafetyContainer;
  bodyboardingSafety?: ForecastSafetyContainer;
  divingSafety?: ForecastSafetyContainer;
  supSafety?: ForecastSafetyContainer;
  swimSafety?: ForecastSafetyContainer;
  sailingSafety?: ForecastSafetyContainer;
} | null;

export type ConditionSafetyPayload = {
  forecast_data?: ForecastSafetyPayload;
  surf_safety?: ForecastSafetyContainer;
  wind_sports_safety?: ForecastSafetyContainer;
  foil_safety?: ForecastSafetyContainer;
  downwind_foil_safety?: ForecastSafetyContainer;
  wing_foil_safety?: ForecastSafetyContainer;
  bodyboarding_safety?: ForecastSafetyContainer;
  diving_safety?: ForecastSafetyContainer;
  sup_safety?: ForecastSafetyContainer;
  swim_safety?: ForecastSafetyContainer;
  sailing_safety?: ForecastSafetyContainer;
} | null | undefined;

/**
 * The only physical condition_scores safety columns in the current schema.
 * Other coastal safety is carried inside forecast_data.sportSafety and
 * forecast_data.safetyState.
 */
export const CONDITION_SAFETY_SELECT_COLUMNS =
  "forecast_data, foil_safety, downwind_foil_safety, wing_foil_safety, " +
  "bodyboarding_safety";

function normaliseSportKey(value: string | null | undefined): string | null {
  const key = value?.toLowerCase().trim().replace(/_/g, "-");
  return key || null;
}

function nonGreenLabel(value: ForecastSafetyIndicator | null | undefined): string | null {
  if (!value) return null;
  const label = value.label?.trim();
  if (!label) return null;
  const level = (value.level ?? value.severity ?? "green").toLowerCase();
  return level === "green" ? null : label;
}

function indicatorSeverity(value: ForecastSafetyIndicator | null | undefined): "green" | "amber" | "red" {
  const level = (value?.level ?? value?.severity ?? "green").toLowerCase();
  if (level === "red" || level === "danger") return "red";
  if (level === "amber" || level === "yellow" || level === "caution" || level === "warning") {
    return "amber";
  }
  return "green";
}

function indicatorsFromContainer(container: ForecastSafetyContainer): ForecastSafetyIndicator[] {
  if (!container) return [];
  return Array.isArray(container)
    ? container
    : [
        ...(container.indicators ?? []),
        ...(container.safety ?? []),
      ];
}

export function safetyStateForIndicators(
  indicators: readonly ForecastSafetyIndicator[] | null | undefined,
): ConditionSafetyState {
  let sawAmber = false;
  let sawUnavailable = false;
  for (const indicator of indicators ?? []) {
    const level = (indicator.level ?? indicator.severity ?? "").toLowerCase().trim();
    if (level === "unavailable" || level === "unknown" || level === "no-data") {
      sawUnavailable = true;
      continue;
    }
    const severity = indicatorSeverity(indicator);
    if (severity === "red") return "flagged-red";
    if (severity === "amber") sawAmber = true;
  }
  if (sawAmber) return "flagged-amber";
  return sawUnavailable ? "no-data" : "clear";
}

function labelsFromContainers(...containers: ForecastSafetyContainer[]): string[] {
  const notes = new Map<string, SafetyNote>();
  for (const container of containers) {
    if (!container) continue;
    const indicators = indicatorsFromContainer(container);
    for (const indicator of indicators) {
      const label = nonGreenLabel(indicator);
      if (!label) continue;
      const severity = indicatorSeverity(indicator);
      if (severity === "green") continue;
      const existing = notes.get(label);
      if (!existing || (existing.severity === "amber" && severity === "red")) {
        notes.set(label, {
          label,
          detail: indicator.detail?.trim() || null,
          severity,
        });
      }
    }
  }
  return [...notes.values()]
    .sort((a, b) => Number(b.severity === "red") - Number(a.severity === "red"))
    .map((note) => note.label);
}

function aliasesForSport(sportKey: string | null): string[] {
  if (!sportKey) return [];
  const aliases = [sportKey];
  if (sportKey === "swim") aliases.push("swimming");
  if (sportKey === "swimming") aliases.push("swim");
  if (sportKey === "wind" || sportKey === "windsurf" || sportKey === "kitesurf") {
    aliases.push("wind", "windsurf", "kitesurf");
  }
  return aliases;
}

function highestSafetyState(
  left: ConditionSafetyState,
  right: ConditionSafetyState,
): ConditionSafetyState {
  const rank: Record<ConditionSafetyState, number> = {
    "no-data": -1,
    clear: 0,
    "flagged-amber": 1,
    "flagged-red": 2,
  };
  return rank[right] > rank[left] ? right : left;
}

function persistedSafetyState(
  forecast: NonNullable<ForecastSafetyPayload>,
  sportKey: string | null,
): ConditionSafetyState | null {
  for (const map of [forecast.safetyState, forecast.safetyStates]) {
    if (!map) continue;
    for (const key of aliasesForSport(sportKey)) {
      const state = map[key];
      if (
        state === "clear" ||
        state === "flagged-amber" ||
        state === "flagged-red" ||
        state === "no-data"
      ) {
        return state;
      }
    }
  }
  return null;
}

function coastalForecastContainers(
  forecast: NonNullable<ForecastSafetyPayload>,
  sportKey: string | null,
): ForecastSafetyContainer[] {
  const sportContainers: ForecastSafetyContainer[] = [];
  if (sportKey) {
    for (const alias of aliasesForSport(sportKey)) {
      sportContainers.push(forecast.sportSafety?.[alias]);
    }
  }

  switch (sportKey) {
    case "surf":
      return [...sportContainers, forecast.surfSafety];
    case "wind":
    case "windsurf":
    case "kitesurf":
      return [...sportContainers, forecast.windSportsSafety, forecast.windSafety];
    case "foil":
      return [...sportContainers, forecast.foilSafety];
    case "downwind-foil":
      return [...sportContainers, forecast.downwindFoilSafety];
    case "wing-foil":
      return [...sportContainers, forecast.wingFoilSafety];
    case "bodyboarding":
      return [...sportContainers, forecast.bodyboardingSafety];
    case "diving":
      return [...sportContainers, forecast.divingSafety];
    case "sup":
      return [...sportContainers, forecast.supSafety];
    case "swim":
    case "swimming":
      return [...sportContainers, forecast.swimSafety];
    case "sailing":
    case "sailing-club":
    case "sailing-hp":
      return [...sportContainers, forecast.sailingSafety];
    default:
      return sportContainers;
  }
}

export function safetyNotesForForecast(
  forecast: ForecastSafetyPayload | null | undefined,
  sport: string | null | undefined,
): string[] {
  if (!forecast) return [];
  const sportKey = normaliseSportKey(sport);
  const notes: string[] = [];

  if (sportKey === "mtb" || sportKey === "mountain-bike") {
    return labelsFromContainers(
      (forecast.mtbSafety ?? []).filter((indicator) => indicator.key !== "trail_damage"),
    );
  }

  notes.push(...labelsFromContainers(...coastalForecastContainers(forecast, sportKey)));

  const snowSport =
    sportKey === "ski" ||
    sportKey === "piste" ||
    sportKey === "snowboard" ||
    sportKey === "off-piste" ||
    sportKey === "offpiste" ||
    sportKey === "touring" ||
    sportKey === "ski-touring";
  if (!snowSport) return notes;

  if (sportKey === "ski" || sportKey === "piste" || sportKey === "snowboard") {
    for (const indicator of forecast.pisteSafety ?? []) {
      const label = nonGreenLabel(indicator);
      if (label) notes.push(label);
    }
  }
  const avalanche = nonGreenLabel(forecast.avalancheSafety);
  if (avalanche) notes.push(avalanche);
  const storm = nonGreenLabel(forecast.stormHazard);
  if (storm) notes.push(storm);
  const snowIndicators = [
    ...(sportKey === "ski" || sportKey === "piste" || sportKey === "snowboard"
      ? forecast.pisteSafety ?? []
      : []),
    ...(forecast.avalancheSafety ? [forecast.avalancheSafety] : []),
    ...(forecast.stormHazard ? [forecast.stormHazard] : []),
  ];
  return labelsFromContainers(snowIndicators);
}

export function safetyStateForForecast(
  forecast: ForecastSafetyPayload | null | undefined,
  sport: string | null | undefined,
): ConditionSafetyState {
  if (!forecast) return "no-data";
  const sportKey = normaliseSportKey(sport);
  const persisted = persistedSafetyState(forecast, sportKey);
  if (persisted === "no-data") return persisted;

  if (sportKey === "mtb" || sportKey === "mountain-bike") {
    const riderSafety = (forecast.mtbSafety ?? []).filter((indicator) => (
      indicator.key !== "trail_damage"
    ));
    const derived = safetyStateForIndicators(riderSafety);
    return persisted ? highestSafetyState(persisted, derived) : derived;
  }

  const containers =
    sportKey === "ski" ||
    sportKey === "piste" ||
    sportKey === "snowboard" ||
    sportKey === "off-piste" ||
    sportKey === "offpiste" ||
    sportKey === "touring" ||
    sportKey === "ski-touring"
      ? snowForecastContainers(forecast, sportKey)
      : coastalForecastContainers(forecast, sportKey);

  const presentContainers = containers.filter((container) => container !== null && container !== undefined);
  if (presentContainers.length === 0) return "no-data";
  const derived = safetyStateForIndicators(presentContainers.flatMap(indicatorsFromContainer));
  return persisted ? highestSafetyState(persisted, derived) : derived;
}

export function safetyNotesForConditionScore(
  row: ConditionSafetyPayload,
  sport: string | null | undefined,
): string[] {
  if (!row) return [];
  const sportKey = normaliseSportKey(sport);
  const forecast = row.forecast_data;
  const topLevel: ForecastSafetyContainer[] = [];

  switch (sportKey) {
    case "surf":
      topLevel.push(row.surf_safety);
      break;
    case "wind":
    case "windsurf":
    case "kitesurf":
      topLevel.push(row.wind_sports_safety);
      break;
    case "foil":
      topLevel.push(row.foil_safety);
      break;
    case "downwind-foil":
      topLevel.push(row.downwind_foil_safety);
      break;
    case "wing-foil":
      topLevel.push(row.wing_foil_safety);
      break;
    case "bodyboarding":
      topLevel.push(row.bodyboarding_safety);
      break;
    case "diving":
      topLevel.push(row.diving_safety);
      break;
    case "sup":
      topLevel.push(row.sup_safety);
      break;
    case "swim":
    case "swimming":
      topLevel.push(row.swim_safety);
      break;
    case "sailing":
    case "sailing-club":
    case "sailing-hp":
      topLevel.push(row.sailing_safety);
      break;
  }

  return [
    ...labelsFromContainers(...topLevel),
    ...safetyNotesForForecast(forecast, sportKey),
  ].filter((note, index, notes) => notes.indexOf(note) === index);
}

export function safetyStateForConditionScore(
  row: ConditionSafetyPayload,
  sport: string | null | undefined,
): ConditionSafetyState {
  if (!row) return "no-data";
  const sportKey = normaliseSportKey(sport);
  const forecast = row.forecast_data;
  const topLevel: ForecastSafetyContainer[] = [];

  switch (sportKey) {
    case "surf":
      topLevel.push(row.surf_safety);
      break;
    case "wind":
    case "windsurf":
    case "kitesurf":
      topLevel.push(row.wind_sports_safety);
      break;
    case "foil":
      topLevel.push(row.foil_safety);
      break;
    case "downwind-foil":
      topLevel.push(row.downwind_foil_safety);
      break;
    case "wing-foil":
      topLevel.push(row.wing_foil_safety);
      break;
    case "bodyboarding":
      topLevel.push(row.bodyboarding_safety);
      break;
    case "diving":
      topLevel.push(row.diving_safety);
      break;
    case "sup":
      topLevel.push(row.sup_safety);
      break;
    case "swim":
    case "swimming":
      topLevel.push(row.swim_safety);
      break;
    case "sailing":
    case "sailing-club":
    case "sailing-hp":
      topLevel.push(row.sailing_safety);
      break;
  }

  const forecastState = safetyStateForForecast(forecast, sportKey);
  if (forecastState !== "no-data") return forecastState;
  const presentTopLevel = topLevel.filter((container) => container !== null && container !== undefined);
  if (presentTopLevel.length === 0) return "no-data";
  return safetyStateForIndicators(presentTopLevel.flatMap(indicatorsFromContainer));
}

/**
 * Like safetyStateForConditionScore, but trusts the writer's persisted
 * per-sport verdict (forecast_data.safetyState / safetyStates) when no
 * indicator containers were stored.
 *
 * Why this is safe: the condition_scores writer stores no indicator
 * containers for a sport it judged clear, so the shared reader reports
 * "no-data" for exactly the spots it cleared. A persisted amber or red still
 * wins, any stored indicators are still evaluated, and the worse of the two
 * verdicts is returned. With neither, the result stays "no-data".
 *
 * Used by the homepage spot cards (app/components/landing/v2/home-conditions.ts).
 * SYNC: if leaderboards or location pages adopt this rule, use this export
 * rather than re-deriving it, so every surface agrees on what "clear" means.
 */
export function safetyStateTrustingPersistedClear(
  row: ConditionSafetyPayload,
  sport: string | null | undefined,
): ConditionSafetyState {
  const derived = safetyStateForConditionScore(row, sport);
  const forecast = row?.forecast_data;
  const persisted = forecast ? persistedSafetyState(forecast, normaliseSportKey(sport)) : null;
  if (derived === "no-data") return persisted ?? "no-data";
  if (!persisted || persisted === "no-data") return derived;
  return highestSafetyState(persisted, derived);
}

function snowForecastContainers(
  forecast: NonNullable<ForecastSafetyPayload>,
  sportKey: string | null,
): ForecastSafetyContainer[] {
  const containers: ForecastSafetyContainer[] = [];
  if (sportKey === "ski" || sportKey === "piste" || sportKey === "snowboard") {
    containers.push(forecast.pisteSafety);
  }
  containers.push(
    forecast.avalancheSafety ? [forecast.avalancheSafety] : undefined,
    forecast.stormHazard ? [forecast.stormHazard] : undefined,
  );
  return containers;
}
